/**
 * EntityIconTile — shared visual primitive for "entity icon in a square
 * tile" appearances. Used by:
 *  - SearchResultItem (search modal results)
 *  - ParentOptionItem (Move/Copy modal destinations)
 *  - EntityTypeBadge (when iconOnly — data-table type column)
 *
 * Centralising avoids three near-identical Center+BowerIcon blocks
 * drifting out of sync. All three previously rendered the same shape
 * with the same NEUTRAL_TILE colours from theme/entityIcons.ts.
 *
 * The tile size scales the icon proportionally:
 *   sm  → 24×24 tile, 14px icon  (compact, inline contexts)
 *   md  → 28×28 tile, 16px icon  (data-table cells, default)
 *   lg  → 32×32 tile, 16px icon  (search results, list items)
 */

import { Center } from '@chakra-ui/react';
import { ENTITY_ICONS } from '../../theme/entityIcons';
import { Glyph } from './Glyph';
import { EntityActivityGlyph } from './EntityActivityGlyph';
import type { EntityType } from '../../theme/entityIcons';
import type { EntityActivity } from '../../utils/entityActivity';

const FALLBACK_CONFIG = ENTITY_ICONS.note;

interface EntityIconTileProps {
  /** Entity type — accepts any string for forward compatibility. Falls
   *  back to the note icon when unrecognised. */
  type: string;
  /** Tile size. Default 'md' (28×28). */
  size?: 'sm' | 'md' | 'lg';
  /** Background work on the entity; swaps the glyph for a spinner or error mark. */
  activity?: EntityActivity;
}

const SIZES = {
  sm: { tile: '24px', icon: '14px' },
  md: { tile: '28px', icon: '16px' },
  lg: { tile: '32px', icon: '16px' },
} as const;

export function EntityIconTile({ type, size = 'md', activity }: EntityIconTileProps) {
  const config = ENTITY_ICONS[type as EntityType] ?? FALLBACK_CONFIG;
  const dims = SIZES[size];

  return (
    <Center
      bg={config.bgColor}
      color={config.color}
      borderRadius="md"
      w={dims.tile}
      h={dims.tile}
      flexShrink={0}
    >
      {/* Identity icon — carries the brand gradient. The tile around it is
          transparent (see NEUTRAL_TILE); it survives only to hold the fixed
          dimensions that keep rows aligned. */}
      <EntityActivityGlyph activity={activity} size={dims.icon}>
        <Glyph {...config.glyph} size={dims.icon} gradient="warm" />
      </EntityActivityGlyph>
    </Center>
  );
}
